import type { ReactNode } from "react";
import clsx from "clsx";
import {
  AlertTriangle,
  CalendarX,
  CheckCircle2,
  Clock,
  Coffee,
  Landmark,
  Loader2,
  Lock,
  PauseCircle,
  RefreshCw,
  VideoOff,
  WifiOff,
  type LucideIcon,
} from "lucide-react";

export type StreamStatus =
  | "loading"
  | "switching"
  | "startingSoon"
  | "notStarted"
  | "notInSession"
  | "waitingForJudge"
  | "betweenMatters"
  | "recess"
  | "paused"
  | "notStreaming"
  | "inCamera"
  | "adjourned"
  | "concluded"
  | "reconnecting"
  | "unavailable";

type Tone = "accent" | "neutral" | "warning" | "danger" | "success";

interface StatusConfig {
  icon: LucideIcon;
  tone: Tone;
  title: string;
  description: string;
  pill?: string;
  /** Icon pulses — used for "something will happen soon" states */
  pulse?: boolean;
  /** Icon spins — used for loading / reconnecting */
  spin?: boolean;
}

const STATUS_CONFIG: Record<StreamStatus, StatusConfig> = {
  loading: {
    icon: Loader2,
    tone: "neutral",
    title: "Loading live stream…",
    description: "Connecting to the courtroom broadcast.",
    spin: true,
  },
  switching: {
    icon: Loader2,
    tone: "neutral",
    title: "Switching stream…",
    description: "The next broadcast will appear in a moment.",
    spin: true,
  },
  startingSoon: {
    icon: Clock,
    tone: "accent",
    title: "Live proceedings will start soon",
    description:
      "The stream will begin automatically when the countdown ends. Please keep this page open.",
    pulse: true,
  },
  notStarted: {
    icon: Clock,
    tone: "accent",
    title: "Live proceedings not yet started",
    description:
      "The stream will begin once the hearing starts. Please stay on this page.",
    pill: "Starts automatically when live",
    pulse: true,
  },
  notInSession: {
    icon: Landmark,
    tone: "neutral",
    title: "Court not in session",
    description:
      "No proceedings are scheduled at present. Please check the cause list for hearing timings.",
  },
  waitingForJudge: {
    icon: Clock,
    tone: "accent",
    title: "Awaiting commencement of proceedings",
    description:
      "The hearing will begin shortly upon the arrival of the Hon'ble Court.",
    pulse: true,
  },
  betweenMatters: {
    icon: Clock,
    tone: "accent",
    title: "Next matter will be taken up shortly",
    description:
      "Proceedings are paused between matters. The stream resumes automatically.",
    pulse: true,
  },
  recess: {
    icon: Coffee,
    tone: "warning",
    title: "Court in recess",
    description:
      "Proceedings will resume after the break. Please stay connected.",
  },
  paused: {
    icon: PauseCircle,
    tone: "warning",
    title: "Live stream temporarily paused",
    description:
      "The broadcast has been paused by order of the Court. It will resume shortly.",
    pill: "Paused by court",
  },
  // Deliberately says nothing about recording — shown to the public when
  // the court is recording but not streaming.
  notStreaming: {
    icon: VideoOff,
    tone: "neutral",
    title: "Live stream not available",
    description:
      "Proceedings in this courtroom are not being streamed live at the moment.",
  },
  inCamera: {
    icon: Lock,
    tone: "danger",
    title: "In-camera proceedings",
    description:
      "This matter is being heard in camera and is not available for public viewing.",
    pill: "Restricted",
  },
  adjourned: {
    icon: CalendarX,
    tone: "neutral",
    title: "Matter adjourned",
    description:
      "This matter has been adjourned. Refer to the cause list for the next date.",
  },
  concluded: {
    icon: CheckCircle2,
    tone: "success",
    title: "Proceedings concluded for the day",
    description: "Today's court session has ended. Thank you for watching.",
  },
  reconnecting: {
    icon: RefreshCw,
    tone: "warning",
    title: "Reconnecting to live stream…",
    description: "Connection interrupted. Retrying automatically.",
    spin: true,
  },
  unavailable: {
    icon: WifiOff,
    tone: "danger",
    title: "Live stream unavailable",
    description:
      "Unable to load the broadcast right now. Please refresh or try again later.",
  },
};

const TONE_CLASSES: Record<Tone, { icon: string; pill: string }> = {
  accent: { icon: "bg-blue-500/15 text-blue-300", pill: "bg-blue-500/15 text-blue-200" },
  neutral: { icon: "bg-white/10 text-slate-300", pill: "bg-white/10 text-slate-200" },
  warning: { icon: "bg-amber-500/15 text-amber-300", pill: "bg-amber-500/15 text-amber-200" },
  danger: { icon: "bg-red-500/15 text-red-300", pill: "bg-red-500/15 text-red-200" },
  success: { icon: "bg-green-500/15 text-green-300", pill: "bg-green-500/15 text-green-200" },
};

export const STREAM_LEGAL_NOTICE =
  "Recording, re-broadcasting or sharing of court proceedings is strictly prohibited and punishable under law.";

export interface StreamStatusAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
}

export interface StreamStatusScreenProps {
  status: StreamStatus;
  /**
   * Both variants share the same dark header / body / footer frame.
   * "page"    — standalone, fills the viewport below the app header (default)
   * "overlay" — fills its parent; for use on top of a video player
   */
  variant?: "page" | "overlay";
  /** e.g. "Court hall 03 · Dr ASJ" — left side of the header */
  courtLabel?: string;
  /** e.g. "CNR: 10155" — right side of the header */
  matterLabel?: string;
  /** Override the default title / description for this status */
  title?: string;
  description?: ReactNode;
  /** Override (or hide with null) the default status pill */
  pill?: string | null;
  /** Extra content under the description, e.g. a countdown */
  children?: ReactNode;
  /** Small text in the footer, above the legal notice */
  footer?: ReactNode;
  action?: StreamStatusAction;
  /** Show the legal notice about recording / sharing (default true) */
  showNotice?: boolean;
  /** Hide when the page already renders a StreamHeaderBar above (default true) */
  showHeader?: boolean;
  className?: string;
}

/**
 * Dark court/matter bar shared by every stream screen — status screens and
 * the live player — so the header looks the same in all states.
 */
export function StreamHeaderBar({
  courtLabel,
  matterLabel,
  className,
}: Readonly<
  Pick<StreamStatusScreenProps, "courtLabel" | "matterLabel" | "className">
>) {
  return (
    <div
      className={clsx(
        "flex min-h-10 shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-black px-4 py-2.5 text-xs text-slate-300 sm:text-sm",
        className,
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <Landmark className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">
          {courtLabel || "Live court proceedings"}
        </span>
      </span>
      {matterLabel && (
        <span className="shrink-0 truncate text-right">{matterLabel}</span>
      )}
    </div>
  );
}

export default function StreamStatusScreen({
  status,
  variant = "page",
  courtLabel,
  matterLabel,
  title,
  description,
  pill,
  children,
  footer,
  action,
  showNotice = true,
  showHeader = true,
  className,
}: Readonly<StreamStatusScreenProps>) {
  const config = STATUS_CONFIG[status];
  const tone = TONE_CLASSES[config.tone];
  const Icon = config.icon;
  const ActionIcon = action?.icon;
  const pillText = pill === undefined ? config.pill : pill;

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        "flex w-full flex-col overflow-hidden bg-black text-white",
        variant === "overlay" ? "h-full" : "min-h-[calc(100vh-70px)]",
        className,
      )}
    >
      {/* Header — always rendered so every status has the same frame */}
      {showHeader && (
        <StreamHeaderBar courtLabel={courtLabel} matterLabel={matterLabel} />
      )}

      {/* Body */}
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-8 text-center">
        <div className="relative mb-2">
          {config.pulse && (
            <span
              className={clsx(
                "absolute inset-0 animate-ping rounded-full opacity-30",
                tone.icon,
              )}
              aria-hidden="true"
            />
          )}
          <div
            className={clsx(
              "relative flex h-14 w-14 items-center justify-center rounded-full",
              tone.icon,
            )}
          >
            <Icon
              className={clsx("h-7 w-7", config.spin && "animate-spin")}
              aria-hidden="true"
            />
          </div>
        </div>

        <h2 className="text-lg font-semibold leading-snug sm:text-2xl">
          {title ?? config.title}
        </h2>
        <p className="max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
          {description ?? config.description}
        </p>

        {children}

        {pillText && (
          <span
            className={clsx(
              "mt-2 rounded-full px-3 py-1 text-xs font-medium",
              tone.pill,
            )}
          >
            {pillText}
          </span>
        )}

        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/20"
          >
            {ActionIcon && <ActionIcon className="h-4 w-4" aria-hidden="true" />}
            {action.label}
          </button>
        )}
      </div>

      {/* Footer */}
      {(footer || showNotice) && (
        <div className="shrink-0 space-y-1 border-t border-white/10 px-4 py-2.5 text-center text-[11px] text-slate-500 sm:text-xs">
          {footer && <div>{footer}</div>}
          {showNotice && (
            <div className="flex items-center justify-center gap-1.5">
              <AlertTriangle
                className="h-3.5 w-3.5 shrink-0"
                aria-hidden="true"
              />
              <span>{STREAM_LEGAL_NOTICE}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
